<!-- eslint-disable no-void -->
<!-- eslint-disable no-void -->
<!-- eslint-disable vue/require-v-for-key -->
<template>
  <div class="promo-banner__wrapper">
    <div
      v-if="animation.type !== 'none'"
      class="promo-banner__toggle-wrapper"
      :class="`promo-banner__toggle-wrapper--${colorTheme}`"
    >
      <button
        class="promo-banner__toggle button button--text"
        type="button"
        @click="handleAnimationToggle"
      >
        <div
          v-if="paused"
          class="promo-banner__play"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.play')"
          />

          <icon-pause />
        </div>

        <div
          v-else
          class="promo-banner__pause"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.pause')"
          />

          <icon-play />
        </div>
      </button>
    </div>

    <component
      :is="wrapperElement"
      v-if="animation.type === 'marquee'"
      :href="useLink ? link : null"
      class="promo-banner critical-component-hide"
      :class="`promo-banner--${colorTheme} promo-banner--${animation.type}`"
      :style="`--marquee-timing: ${animation.marqueeTiming * animation.marqueeRepetition}s;`"
      @click="handleModalToggle"
    >
      <ul
        v-for="(listIndex, bannerIndex) of marqueeBannerRenders"
        :key="bannerIndex"
        :aria-hidden="isAriaHidden(listIndex)"
        class="promo-banner__list"
        :class="{'promo-banner__list--paused': paused}"
      >
        <li
          v-for="(block, index) of blocks"
          :key="`promo-${index}`"
          class="promo-banner__list-item text-other-label-s"
        >
          <span
            v-if="block.title"
            v-html="getBlockText(block.title, isAriaHidden(listIndex))"
          />
          <span
            v-if="block.caption"
            class="promo-banner__list-item__block"
            v-html="getBlockText(block.caption, isAriaHidden(listIndex))"
          />
          <span
            v-if="block.caption_2"
            class="promo-banner__list-item__block"
            v-html="getBlockText(block.caption_2, isAriaHidden(listIndex))"
          />
        </li>
      </ul>

      <btn
        v-if="useModal"
        class="accessibility__sign-post"
        type="button"
        show-icon
        :label="$string('accessibility.open')"
        @click.prevent="handleModalToggle"
        @keydown.enter="handleModalToggle"
      />
    </component>

    <component
      :is="wrapperElement"
      v-else
      :href="useLink ? link : null"
      class="promo-banner critical-component-hide"
      :class="`promo-banner--${colorTheme} promo-banner--${animation.type}`"
      @click="handleModalToggle"
    >
      <span
        class="visually-hidden"
        v-text="$string('general.site_header.promo_banner')"
      />
      <div class="promo-banner__wrapped">
        <button
          v-if="animation?.type !== 'none'"
          class="promo-banner__arrow-left"
          type="button"
          @click="goToPreviousBanner"
        >
          <sharp-left-arrow />
        </button>
        <div class="promo-banner__slider-container">
          <ul
            ref="promoList"
            class="promo-banner__list"
            :class="{'promo-banner__list--paused': paused}"
            :style="autoscrollStyle"
          >
            <li
              v-for="(block, index) of duplicatedBlocks"
              :key="`promo-${index}`"
              :class="{
                'active': index === autoscrollIndex,
                'inactive': index !== autoscrollIndex
              }"
              class="promo-banner__list-item text-other-label-s"
            >
              <span
                v-if="block.title"
                v-html="getBlockText(block.title)"
              />
              <span
                v-if="block.caption"
                v-html="getBlockText(block.caption)"
              />
              <span
                v-if="block.caption_2"
                v-html="getBlockText(block.caption_2)"
              />
            </li>
          </ul>
        </div>
        <button
          v-if="animation?.type !== 'none'"
          type="button"
          class="promo-banner__arrow-right"
          @click="goToNextBanner"
        >
          <sharp-right-arrow />
        </button>
      </div>

      <btn
        v-if="useModal"
        class="accessibility__sign-post"
        show-icon
        type="button"
        :label="$string('accessibility.open')"
        @click.prevent="handleModalToggle"
        @keydown.enter="handleModalToggle"
      />
    </component>

    <overlay
      v-if="useModal"
      key="promoBanner"
      ref="modal"
      block-class="promo-banner-overlay"
      direction="left"
      namespace="promoBanner"
      type="drawer"
    >
      <template
        v-if="modal.title"
        #title
      >
        <div
          class="promo-banner__modal-title"
          v-html="modal.title"
        />
      </template>

      <template
        v-if="modal.caption || modal.image"
        #body
      >
        <div
          v-if="modal.caption"
          class="promo-banner__modal-caption"
          v-html="modal.caption"
        />

        <div class="promo-banner__modal-image-wrapper">
          <responsive-image
            v-if="modal.image"
            :image="modal.image"
            min-max="200-500"
            class="promo-banner__modal-image"
          />
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - Conditionally loaded when section has blocks using Liquid.
 *
 * @param {Object} animation - Animation settings.
 * @param {Array} blocks - Promo blocks.
 * @param {String} colorTheme - 'light' or 'dark' based on section setting.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 *
 */

import { mapActions } from 'vuex'
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'
import IconPlay from '~icons/general/new-play.svg'
import IconPause from '~icons/general/new-pause.svg'
import SharpLeftArrow from '~icons/directional-navigation/arrow/sharp-left-arrow.svg'
import SharpRightArrow from '~icons/directional-navigation/arrow/sharp-right-arrow.svg'

export default {
  name: 'PromoBanner',

  components: {
    Btn,
    Overlay,
    ResponsiveImage,
    IconPause,
    IconPlay,
    SharpLeftArrow,
    SharpRightArrow,
  },

  props: {
    animation: {
      type: Object,
      default: () => ({
        type: 'none',
        autoscrollTiming: 5,
        marqueeTiming: 20,
        marqueeRepetition: 3,
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    link: {
      type: String,
      default: '',
    },
    colorTheme: {
      type: String,
      default: 'light',
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    modal: {
      type: Object,
      default: () => ({
        title: '',
        caption: '',
        image: () => ({}),
      }),
    },
  },

  data() {
    return {
      paused: false,
      autoscrollIndex: 0,
      autoscrollAnimationDuration: 500,
      autoscrollInterval: null,
      currentIndex: 0,
      nextBannerInterval: null,
    }
  },

  computed: {
    duplicatedBlocks() {
      return [...this.blocks, ...this.blocks]
    },

    /**
     * Compute total # of times promo list should render for marquee scroll.
     * @returns {Number}
     */
    marqueeBannerRenders() {
      return this.animation.marqueeRepetition + 1
    },

    /**
     * Computes whether to use URL for link.
     * @returns {Boolean}
     */
    useLink() {
      return this.link && !this.useModal
    },

    /**
     * Computes whether to use modal.
     * @returns {Boolean}
     */
    useModal() {
      return this.modal.title || this.modal.caption || this.modal.image
    },

    /**
     * Computes whether modal is active.
     * @returns {Boolean}
     */
    isModalActive() {
      return this.useModal && this.$refs.modal.isActive
    },

    /**
     * Computes what wrapper element should be.
     * @returns {String}
     */
    wrapperElement() {
      return this.useLink ? 'a' : 'div'
    },

    /**
     * Compute the position of autoscroll cells, based on current index
     */
    autoscrollStyle() {
      return {
        transform: `translateX(calc(-50vw * ${this.autoscrollIndex}))`,
        transition: this.autoscrollInterval ? 'none' : `transform ${this.autoscrollAnimationDuration}ms ease-in-out`,
      }
    },
  },

  async mounted() {
    if (this.animation.type === 'autoscroll') {
      this.startAutoscroll()
    }

    await this.$nextTick()
    cnvs.EventBus.emit('cnvs:update-site-header-height')
  },

  beforeUnmount() {
    clearInterval(this.nextBannerInterval)
    clearInterval(this.autoscrollInterval)
  },

  methods: {

    /**
     * Get block text.
     * - Applies text class to <a> elements.
     * @param {String} block - Block text.
     * @param {Boolean} isAriaHidden - If parent has attr `aria-hidden`.
     * @returns {String}
     */
    goToPreviousBanner() {
      event.preventDefault()
      event.stopPropagation()
      if (this.animation.type === 'autoscroll') {
        this.stopAutoscroll()
        this.autoscrollIndex--
        if (this.autoscrollIndex < 0) {
          this.$refs.promoList.style.transform = `translateX(calc(-50vw * ${this.blocks.length - 1}))`
          this.autoscrollIndex = this.blocks.length - 1
          // eslint-disable-next-line no-void
          void this.$refs.promoList.offsetHeight

          this.$refs.promoList.style.transition = `transform ${this.autoscrollAnimationDuration}ms ease-in-out`
          this.$nextTick().then(() => {
            this.$refs.promoList.style.transition = `transform ${this.autoscrollAnimationDuration}ms ease-in-out`
            this.updateAutoscroll()
            if (!this.paused) {
              this.startAutoscroll()
            }
          })
        } else {
          this.updateAutoscroll()
          if (!this.paused) {
            this.startAutoscroll()
          }
        }
      } else if (this.animation.type === 'none') {
        this.currentIndex--
        if (this.currentIndex < 0) {
          this.currentIndex = this.blocks.length - 1
        }
        this.$refs.promoList.style.transform = `translateX(calc(-50vw * ${this.currentIndex}))`
      } else if (this.currentIndex > 0) {
        this.currentIndex--
      } else {
        this.currentIndex = this.blocks.length - 1
      }
    },
    goToNextBanner() {
      event.preventDefault()
      event.stopPropagation()
      if (this.animation.type === 'autoscroll') {
        this.stopAutoscroll()
        this.autoscrollIndex++
        if (this.autoscrollIndex >= this.blocks.length) {
          this.autoscrollIndex = 0
          this.$refs.promoList.style.transform = `translateX(0)`
          this.autoscrollIndex = 0
          // eslint-disable-next-line no-void
          void this.$refs.promoList.offsetHeight

          this.$refs.promoList.style.transition = `transform ${this.autoscrollAnimationDuration}ms ease-in-out`
          this.$nextTick().then(() => {
            this.$refs.promoList.style.transition = `transform ${this.autoscrollAnimationDuration}ms ease-in-out`
            this.updateAutoscroll()
            if (!this.paused) {
              this.startAutoscroll()
            }
          })
        } else {
          this.updateAutoscroll()
          if (!this.paused) {
            this.startAutoscroll()
          }
        }
      } else if (this.animation.type === 'none') {
        this.currentIndex++
        if (this.currentIndex >= this.blocks.length) {
          this.currentIndex = 0
        }
        this.$refs.promoList.style.transform = `translateX(calc(-50vw * ${this.currentIndex}))`
      } else if (this.currentIndex < this.blocks.length - 1) {
        this.currentIndex++
      } else {
        this.currentIndex = 0
      }
    },
    updateAutoscroll() {
      this.$refs.promoList.style.transform = `translateX(calc(-50vw * ${this.autoscrollIndex}))`
    },
    getBlockText(block, isAriaHidden = false) {
      const anchorLinkReplacement = isAriaHidden
        ? '<a class="text-other-label-s" tabindex="-1"'
        : '<a class="text-other-label-s"'

      return block
        .replaceAll('<a', anchorLinkReplacement)
        .replaceAll('<strong', '<strong class="text-other-label-s"')
    },

    /**
     * Return if element should be `aria-hidden`.
     * @param index - Target index.
     * @returns {Boolean}
     */
    isAriaHidden(index) {
      return index > 1
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle modal toggle.
     */
    handleModalToggle() {
      if (!this.useModal) {
        return
      }

      this.toggleOverlay({
        component: 'promo-banner',
        ignoreDismissed: true,
        namespace: 'promoBanner',
      })
    },

    /**
     * Handle play/pause animation toggle.
     */
    handleAnimationToggle() {
      this.paused = !this.paused

      if (this.animation.type === 'autoscroll') {
        if (this.paused) {
          this.stopAutoscroll()
        } else {
          this.startAutoscroll(true)
        }
      }
    },

    /**
     * Start autoscroll interval
     * @param {Boolean} scrollImmediately Trigger scrolling immediately
     */
    startAutoscroll(scrollImmediately = false) {
      if (scrollImmediately) {
        this.autoscroll()
      }

      this.autoscrollInterval =
        setInterval(this.autoscroll, this.animation.autoscrollTiming * 1000)
    },

    /**
     * Stop autoscroll interval
     */
    stopAutoscroll() {
      clearInterval(this.autoscrollInterval)
      this.autoscrollInterval = null
    },

    /**
     * When autoscroll is enabled, change the banner on interval.
     */
    autoscroll() {
      this.autoscrollIndex++
      const totalItems = this.blocks.length

      if (this.autoscrollIndex >= totalItems) {
        this.$refs.promoList.style.transition = 'none'
        this.$refs.promoList.style.transform = `translateX(0)`

        // eslint-disable-next-line no-void
        void this.$refs.promoList.offsetHeight

        this.autoscrollIndex = 0
        this.$nextTick().then(() => {
          this.$refs.promoList.style.transition = `transform ${this.autoscrollAnimationDuration}ms ease-in-out`
          this.$refs.promoList.style.transform = `translateX(calc(-50vw * ${this.autoscrollIndex}))`
        })
      } else {
        this.updateAutoscroll()
      }
    },

  },
}
</script>

<style lang="scss">
@import './promo-banner';
</style>
